import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { BadgeCheck, XCircle, Loader2, Clock } from "lucide-react";
import Logo from "./Logo";
import { API } from "@/config/site";

export default function PaymentResult({ canceled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(canceled ? "canceled" : "processing");

  useEffect(() => {
    if (canceled || !sessionId) return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          clearInterval(t);
        } else if (["failed", "expired"].includes(data.payment_status) || data.status === "expired") {
          setState("failed");
          clearInterval(t);
        }
      } catch {
        // keep polling
      }
      if (tries > 20) {
        setState("timeout");
        clearInterval(t);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [canceled, sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <nav className="flex items-center justify-between px-5 sm:px-10 py-6 border-b border-[#1a1a1a]">
        <Logo />
        <span className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-[#E60000] border border-[#E60000]/40 px-3 py-2">
          CARD PAYMENT
        </span>
      </nav>
      <main className="flex-1 flex items-center justify-center px-5">
        <div className="max-w-md w-full text-center space-y-5" data-testid="payment-result">
          {state === "processing" && (
            <>
              <Loader2 size={56} className="mx-auto text-[#E60000] animate-spin" />
              <h1 className="font-display uppercase text-3xl">Confirming payment</h1>
              <p className="font-mono text-xs text-[#888] leading-relaxed">
                Hang tight - we are confirming your card payment with Stripe.
              </p>
            </>
          )}
          {state === "paid" && (
            <>
              <BadgeCheck size={64} className="mx-auto text-[#E60000]" />
              <h1 className="font-display uppercase text-3xl" data-testid="payment-success-title">
                Payment confirmed
              </h1>
              <p className="font-mono text-xs text-[#999] leading-relaxed">
                Your card payment went through and your order is verified automatically - no code
                needed. You will get an email when it ships. Screenshot this page.
              </p>
            </>
          )}
          {state === "canceled" && (
            <>
              <XCircle size={56} className="mx-auto text-[#888]" />
              <h1 className="font-display uppercase text-3xl" data-testid="payment-canceled-title">
                Payment canceled
              </h1>
              <p className="font-mono text-xs text-[#999] leading-relaxed">
                No charge was made. Your order is still saved - go back and pay whenever you are
                ready.
              </p>
            </>
          )}
          {(state === "failed" || state === "timeout") && (
            <>
              <Clock size={56} className="mx-auto text-[#E60000]" />
              <h1 className="font-display uppercase text-3xl" data-testid="payment-pending-title">
                Still processing
              </h1>
              <p className="font-mono text-xs text-[#999] leading-relaxed">
                We could not confirm the payment yet. If your card was charged, your order is safe -
                check your email or contact us.
              </p>
            </>
          )}
          <Link
            to="/"
            data-testid="back-to-store-btn"
            className="inline-block bg-white text-black font-display uppercase tracking-wide text-lg px-8 py-4 hover:bg-[#E60000] hover:text-white transition-colors"
          >
            Back to the drop
          </Link>
        </div>
      </main>
    </div>
  );
}
